/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { graphql } from 'react-relay';
import { useEffect, useContext, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

import type { useQueryInvitationUrlCreateInvitationUrlQuery as useQueryInvitationUrlCreateInvitationUrlQuery$data } from './__types__/useQueryInvitationUrlCreateInvitationUrlQuery.ts';

type UseQueryInvitationUrlProps = { skipQuery?: boolean };

type InvitationUrlResult = {
	existingInviteLink: string;
	existingInviteLinkStatus: string | undefined;
	loading: boolean;
	error: any;
};

type AdminAccessUrlsQuery$data = {
	admin_accessUrls: {
		edges: Array<{
			node: {
				id: string;
				resourceUrl: string;
				expirationDate: string;
				url: string;
				status: string;
			};
		}>;
		pageInfo: {
			endCursor: string | null;
			hasNextPage: boolean;
			startCursor: string | null;
		};
	};
};

type AdminAccessUrlsQuery$variables = {
	orgId: string;
	after?: string | null;
	first?: number | null;
};

type AccessUrlNode = AdminAccessUrlsQuery$data['admin_accessUrls']['edges'][0]['node'];

/**
 * CC GraphQL query abstraction for fetching invitation URLs
 */
const useCCInvitationUrlQuery = (
	skipQuery: boolean,
	isLicensed: boolean,
	isAdmin: boolean,
): InvitationUrlResult => {
	const { data, loading, error } = useQuery<useQueryInvitationUrlCreateInvitationUrlQuery$data>(
		/** @schema cc */ gql`
			query useQueryInvitationUrlCreateInvitationUrlQuery {
				invitationUrls {
					urls {
						id
						url
						status
					}
				}
			}
		`,
		{
			skip: skipQuery || !isLicensed || !isAdmin || fg('confluence_frontend_use_agg_invite_url'),
		},
	);

	const existingInviteLink = data?.invitationUrls?.urls?.[0]?.url || '';
	const existingInviteLinkStatus = data?.invitationUrls?.urls?.[0]?.status;

	return {
		existingInviteLink,
		existingInviteLinkStatus,
		loading,
		error,
	};
};

/**
 * AGG query abstraction with automatic pagination to find matching invitation URL
 */
const FINISHED_NO_MATCH = 'FINISHED_NO_MATCH' as const;
type SearchResult = AccessUrlNode | null | typeof FINISHED_NO_MATCH;

const useAGGInvitationUrlQuery = (
	skipQuery: boolean,
	isLicensed: boolean,
	isAdmin: boolean,
): InvitationUrlResult => {
	// Single state: null = still searching, AccessUrlNode = found match, FINISHED_NO_MATCH = finished but no match
	const [searchResult, setSearchResult] = useState<SearchResult>(null);
	const { orgId } = useSessionData();

	const {
		data: aggData,
		loading: aggLoading,
		error: aggError,
		refetch,
	} = useQueryInterop<AdminAccessUrlsQuery$data, AdminAccessUrlsQuery$variables>(
		/** @schema agg */ graphql`
			query useQueryInvitationUrlQuery($orgId: ID!, $after: String, $first: Int) {
				admin_accessUrls(orgId: $orgId, after: $after, first: $first) {
					edges {
						node {
							id
							resourceUrl
							expirationDate
							url
							status
						}
					}
					pageInfo {
						endCursor
						hasNextPage
						startCursor
					}
				}
			}
		`,
		{
			skip:
				skipQuery ||
				!isLicensed ||
				!isAdmin ||
				!orgId ||
				!fg('confluence_frontend_use_agg_invite_url'),
			variables: {
				orgId: orgId || '',
				first: 50,
			},
		},
	);

	// Reset search state when orgId changes
	useEffect(() => {
		setSearchResult(null);
	}, [orgId]);

	// Handle pagination and matching logic
	useEffect(() => {
		// Skip if no data, still loading, or already finished (found match or finished searching)
		if (!aggData || aggLoading || searchResult !== null) {
			return;
		}

		const match = findMatchingNode(aggData.admin_accessUrls.edges);
		if (match) {
			setSearchResult(match);
			return;
		}

		// If no match and has more pages, fetch next page
		const { hasNextPage, endCursor } = aggData.admin_accessUrls.pageInfo;
		if (hasNextPage && endCursor && refetch) {
			void refetch({
				orgId: orgId || '',
				after: endCursor,
				first: 50,
			});
		} else {
			setSearchResult(FINISHED_NO_MATCH);
		}
	}, [aggData, aggLoading, orgId, refetch, searchResult]);

	const matchedNode: AccessUrlNode | null =
		searchResult !== null && searchResult !== FINISHED_NO_MATCH ? searchResult : null;

	return {
		existingInviteLink: matchedNode?.url || '',
		existingInviteLinkStatus: matchedNode?.status,
		loading: aggLoading || searchResult === null,
		error: aggError,
	};
};

/**
 * Main hook that uses feature flag to choose between CC and AGG query implementations
 */
export const useQueryInvitationUrl = ({ skipQuery = false }: UseQueryInvitationUrlProps) => {
	const isLicensed = useIsLicensed();

	const {
		isSiteAdmin,
		loading: spaViewContextLoading,
		error: spaViewContextError,
	} = useContext(SPAViewContext);

	const isAdmin = isSiteAdmin && !spaViewContextLoading && !spaViewContextError;

	// CC query abstraction
	const ccResult = useCCInvitationUrlQuery(skipQuery, isLicensed, isAdmin);

	// AGG query abstraction with pagination
	const aggResult = useAGGInvitationUrlQuery(skipQuery, isLicensed, isAdmin);

	// Select result based on feature flag (inline feature gate call)
	const result = fg('confluence_frontend_use_agg_invite_url') ? aggResult : ccResult;

	// Handle error marking
	useEffect(() => {
		if (result.error && isUnauthorizedError(result.error)) {
			markErrorAsHandled(result.error);
		}
	}, [result.error]);

	return result;
};

/**
 * Find matching invitation URL node by comparing hostname from resourceUrl with current page hostname
 */
const findMatchingNode = (
	edges: AdminAccessUrlsQuery$data['admin_accessUrls']['edges'],
): AccessUrlNode | null => {
	const currentHostname = typeof window !== 'undefined' ? window.location.hostname : null;
	for (const edge of edges) {
		let resourceHostname: string | null = null;
		try {
			resourceHostname = new URL(edge.node.resourceUrl).hostname;
		} catch {
			resourceHostname = null;
		}
		if (resourceHostname === currentHostname) {
			return edge.node;
		}
	}
	return null;
};
